import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Typography, Chip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import WIPBatchList from "../components/WIP/WIPBatchList.component";
import { getWipTrackerDetails } from "../reducers/wip.reducer";
import { selectUserInfo, selectUserId } from "../selectors/login.selector";

const WIPTrackerDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { wipId } = useParams();

  // Get query params for filtering (Excel-style multi-row view)
  const queryParams = new URLSearchParams(location.search);
  const trackerId = queryParams.get("trackerId"); // Item ID from query params
  const season = queryParams.get("season");
  const factoryId = queryParams.get("factoryId");
  const supplierId = queryParams.get("supplierId");
  const isSupplierView = queryParams.get("supplier") === "true";

  // Get data from Redux
  const trackerDetails = useSelector((state) => state.wip.trackerDetails) || [];
  const loading = useSelector((state) => state.wip.loading);
  const saving = useSelector((state) => state.wip.saving);
  const userInfo = useSelector(selectUserInfo);
  const currentUserId = useSelector(selectUserId);
  const currentUserName = userInfo?.name;

  // Load tracker details on mount
  useEffect(() => {
    if (wipId || trackerId) {
      // Single item view (using wipId from path or trackerId from query)
      dispatch(getWipTrackerDetails({ trackerId: wipId || trackerId }));
    } else {
      // Excel-style view: fetch multiple items with filters
      dispatch(
        getWipTrackerDetails({
          season,
          factoryId,
          supplierId,
        })
      );
    }
  }, [wipId, trackerId, season, factoryId, supplierId, dispatch]);

  // Determine back path
  const getBackPath = () => {
    if (isSupplierView) {
      return "/wip-vendor";
    }
    return "/wip";
  };

  // Loading state
  if (loading && trackerDetails.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading WIP tracker details...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#ffffff",
      }}
    >
      {/* Back Button - Above WIPBatchList */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(getBackPath())}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            borderColor: "#d0d5dd",
            color: "#667085",
            "&:hover": {
              borderColor: "#667085",
              backgroundColor: "#f5f7fa",
            },
          }}
        >
          Back
        </Button>
        {isSupplierView && (
          <Chip
            label="Vendor View"
            size="small"
            sx={{
              bgcolor: "#E0E7FF",
              color: "#3730A3",
              fontWeight: 600,
              fontSize: "13px",
              height: "28px",
              px: 1,
            }}
          />
        )}
      </Box>

      {/* Use WIPBatchList component with trackerDetails data */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <WIPBatchList
          items={trackerDetails}
          loading={loading}
          saving={saving}
          currentUserId={currentUserId}
          currentUserName={currentUserName}
          readOnly={true} // Supplier platform users can only view comments, not edit
          backPath={getBackPath()}
        />
      </Box>
    </Box>
  );
};

export default WIPTrackerDetails;
